import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../services/api';
import {
  FormContainer,
  FormGroup,
  Label,
  Description,
  Select,
  ButtonContainer,
  CancelButton,
  SubmitButton,
  ErrorMessage,
  LoadingMessage,
} from './JustificativaVacinaForm.styles';

const JustificativaVacinaForm = ({ gestanteCpf, gravidezId, tipoVacina, preselectedJustificativaId, onClose, onSuccess }) => {
  const [justificativas, setJustificativas] = useState([]);
  const [selectedJustificativa, setSelectedJustificativa] = useState(preselectedJustificativaId ? String(preselectedJustificativaId) : '');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJustificativas();
  }, []);

  const fetchJustificativas = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/vacinas/tipos-justificativas');
      
      const justificativasFiltradas = response.data.filter(
        justificativa => justificativa.tipo_vacina.nome === tipoVacina.nome
      );
      
      setJustificativas(justificativasFiltradas);

      if (preselectedJustificativaId && justificativasFiltradas.some(j => j.id === preselectedJustificativaId)) {
        setSelectedJustificativa(String(preselectedJustificativaId));
      }
    } catch (err) {
      console.error('Erro ao buscar justificativas:', err);
      setError('Erro ao carregar as justificativas.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedJustificativa) {
      setError('Por favor, selecione uma justificativa.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.post('/vacinas/justificativas', {
        cpf_gestante: gestanteCpf,
        gestacao_id: gravidezId,
        tipo_justificativa_id: parseInt(selectedJustificativa)
      });

      if (onSuccess) {
        onSuccess('Justificativa registrada com sucesso!');
      }
      onClose();
    } catch (err) {
      console.error('Erro ao salvar justificativa:', err);
      const errorMessage = err.response?.data?.detail || 'Erro ao salvar a justificativa.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <FormContainer>
        <LoadingMessage>Carregando justificativas...</LoadingMessage>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Description>
        Selecione a justificativa para a vacinação incompleta da gestante
      </Description>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="justificativa">Justificativa</Label>
          <Select
            id="justificativa"
            value={selectedJustificativa}
            onChange={(e) => setSelectedJustificativa(e.target.value)}
            disabled={submitting}
            required
          >
            <option value="">Selecione a justificativa desejada</option>
            {justificativas.map((justificativa) => (
              <option key={justificativa.id} value={justificativa.id}>
                {justificativa.justificativa}
              </option>
            ))}
          </Select>
        </FormGroup>

        <ButtonContainer>
          <CancelButton type="button" onClick={onClose} disabled={submitting}>
            Cancelar e voltar
          </CancelButton>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Salvando...' : 'Confirmar e salvar'}
          </SubmitButton>
        </ButtonContainer>
      </form>
    </FormContainer>
  );
};

JustificativaVacinaForm.propTypes = {
  gestanteCpf: PropTypes.string.isRequired,
  gravidezId: PropTypes.number.isRequired,
  tipoVacina: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nome: PropTypes.string.isRequired,
  }).isRequired,
  preselectedJustificativaId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

JustificativaVacinaForm.defaultProps = {
  preselectedJustificativaId: null,
  onSuccess: null,
};

export default JustificativaVacinaForm;
